import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import RequireAuth from './components/auth/RequireAuth';
import RequireRole from './components/auth/RequireRole';
import DashboardLayout from './components/dashboard/DashboardLayout';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import AllServicesPage from './pages/AllServicesPage';
import ServiceTypePage from './pages/ServiceTypePage';
import DocumentsPage from './pages/DocumentsPage';
import OrderPage from './pages/OrderPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import TranslatorRegisterPage from './pages/TranslatorRegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import FAQPage from './pages/FAQPage';
import HowItWorksPage from './pages/HowItWorksPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import DirectoryPage from './pages/DirectoryPage';
import TranslatorPage from './pages/TranslatorPage';
import LanguagesPage from './pages/LanguagesPage';
import QuoteWizardPage from './pages/QuoteWizardPage';
import TrackOrderPage from './pages/TrackOrderPage';
import PaymentReturnPage from './pages/PaymentReturnPage';
import ClaimAccountPage from './pages/ClaimAccountPage';
import LegalPage from './pages/LegalPage';
import {
  ClientHomePage,
  ClientOrdersPage,
  ClientOrderDetailPage,
  ClientDocumentsPage,
  ClientMessagesPage,
  ClientNotificationsPage,
  ClientProfilePage,
  ClientPaymentsPage,
} from './pages/app/ClientSpacePages';
import NewOrderPage from './pages/app/NewOrderPage';
import PlaceholderPage from './pages/PlaceholderPage';
import {
  LegalHomePage,
  LegalVerificationsPage,
  LegalVerificationDetailPage,
  LegalProfilePage,
  LegalNotificationsPage,
  LegalMessagesPage,
} from './pages/legal/LegalSpacePages';
import {
  TranslatorHomePage,
  TranslatorMissionsPage,
  TranslatorMissionDetailPage,
  TranslatorProfilePage,
  TranslatorNotificationsPage,
  TranslatorMessagesPage,
  TranslatorAvailabilityPage,
} from './pages/pro/TranslatorSpacePages';
import {
  AdminHomePage,
  AdminOrdersPage,
  AdminUsersPage,
  AdminTranslatorsPage,
  AdminClientsPage,
  AdminClientProfilePage,
} from './pages/admin/AdminSpacePages';
import AdminTranslatorProfilePage from './pages/admin/AdminTranslatorProfilePage';
import AdminOrderDetailPage from './pages/admin/AdminOrderDetailPage';
import AdminVerifiersPage from './pages/admin/AdminVerifiersPage';
import AdminReviewsPage from './pages/admin/AdminReviewsPage';
import AdminVerifierProfilePage from './pages/admin/AdminVerifierProfilePage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminProfilePage from './pages/admin/AdminProfilePage';
import {
  AdminQuotesPage,
  AdminLanguagesPage,
  AdminDeliveryPage,
  AdminServicesPage,
  AdminInvoicesPage,
  AdminMessagesPage,
  AdminPaymentsPage,
  AdminNotificationsPage,
  AdminEmailLogsPage,
} from './pages/admin/AdminOpsPages';

function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/tous-les-services" element={<AllServicesPage />} />
        <Route path="/services/type/:typeId" element={<ServiceTypePage />} />
        <Route path="/services/:slug" element={<Navigate to="/tous-les-services" replace />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/cart" element={<Navigate to="/order" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/devenir-traducteur" element={<TranslatorRegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/order" element={<OrderPage />} />
        <Route path="/suivi" element={<TrackOrderPage />} />
        <Route path="/paiement/retour" element={<PaymentReturnPage />} />
        <Route path="/activer-compte" element={<ClaimAccountPage />} />
        <Route path="/devis" element={<QuoteWizardPage />} />
        <Route path="/quote" element={<QuoteWizardPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/translator" element={<TranslatorPage />} />
        <Route path="/languages" element={<LanguagesPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/privacy" element={<LegalPage />} />
        <Route path="/annuaire" element={<DirectoryPage />} />
      </Route>

      <Route
        path="/app"
        element={(
          <RequireAuth>
            <RequireRole allow={['client', 'admin']}>
              <DashboardLayout space="client" />
            </RequireRole>
          </RequireAuth>
        )}
      >
        <Route index element={<ClientHomePage />} />
        <Route path="new-order" element={<NewOrderPage />} />
        <Route path="orders" element={<ClientOrdersPage />} />
        <Route path="orders/:id" element={<ClientOrderDetailPage />} />

        <Route path="documents" element={<ClientDocumentsPage />} />
        <Route path="messages" element={<ClientMessagesPage />} />
        <Route path="notifications" element={<ClientNotificationsPage />} />
        <Route path="payments" element={<ClientPaymentsPage />} />
        <Route path="profile" element={<ClientProfilePage />} />
        <Route path="*" element={<PlaceholderPage />} />
      </Route>

      <Route
        path="/pro"
        element={(
          <RequireAuth>
            <RequireRole allow={['translator', 'admin']}>
              <DashboardLayout space="translator" />
            </RequireRole>
          </RequireAuth>
        )}
      >
        <Route index element={<TranslatorHomePage />} />
        <Route path="missions" element={<TranslatorMissionsPage />} />
        <Route path="missions/:id" element={<TranslatorMissionDetailPage />} />
        <Route path="availability" element={<TranslatorAvailabilityPage />} />
        <Route path="messages" element={<TranslatorMessagesPage />} />
        <Route path="notifications" element={<TranslatorNotificationsPage />} />
        <Route path="profile" element={<TranslatorProfilePage />} />
        <Route path="*" element={<PlaceholderPage />} />
      </Route>

      <Route
        path="/legal"
        element={(
          <RequireAuth>
            <RequireRole allow={['lawyer', 'notary', 'admin']}>
              <DashboardLayout space="legal" />
            </RequireRole>
          </RequireAuth>
        )}
      >
        <Route index element={<LegalHomePage />} />
        <Route path="verifications" element={<LegalVerificationsPage />} />
        <Route path="verifications/:id" element={<LegalVerificationDetailPage />} />
        <Route path="assignments" element={<LegalVerificationsPage />} />
        <Route path="messages" element={<LegalMessagesPage />} />
        <Route path="notifications" element={<LegalNotificationsPage />} />
        <Route path="profile" element={<LegalProfilePage />} />
        <Route path="*" element={<PlaceholderPage />} />
      </Route>

      <Route
        path="/lawyer"
        element={(
          <RequireAuth>
            <RequireRole allow={['lawyer', 'admin']}>
              <DashboardLayout space="lawyer" />
            </RequireRole>
          </RequireAuth>
        )}
      >
        <Route index element={<LegalHomePage />} />
        <Route path="verifications" element={<LegalVerificationsPage />} />
        <Route path="verifications/:id" element={<LegalVerificationDetailPage />} />
        <Route path="assignments" element={<LegalVerificationsPage />} />
        <Route path="messages" element={<LegalMessagesPage />} />
        <Route path="notifications" element={<LegalNotificationsPage />} />
        <Route path="profile" element={<LegalProfilePage />} />
        <Route path="*" element={<PlaceholderPage />} />
      </Route>

      <Route
        path="/notary"
        element={(
          <RequireAuth>
            <RequireRole allow={['notary', 'admin']}>
              <DashboardLayout space="notary" />
            </RequireRole>
          </RequireAuth>
        )}
      >
        <Route index element={<LegalHomePage />} />
        <Route path="verifications" element={<LegalVerificationsPage />} />
        <Route path="verifications/:id" element={<LegalVerificationDetailPage />} />
        <Route path="assignments" element={<LegalVerificationsPage />} />
        <Route path="messages" element={<LegalMessagesPage />} />
        <Route path="notifications" element={<LegalNotificationsPage />} />
        <Route path="profile" element={<LegalProfilePage />} />
        <Route path="*" element={<PlaceholderPage />} />
      </Route>

      <Route
        path="/admin"
        element={(
          <RequireAuth>
            <RequireRole allow="admin">
              <DashboardLayout space="admin" />
            </RequireRole>
          </RequireAuth>
        )}
      >
        <Route index element={<AdminHomePage />} />
        <Route path="orders" element={<AdminOrdersPage />} />
        <Route path="orders/:id" element={<AdminOrderDetailPage />} />
        <Route path="quotes" element={<AdminQuotesPage />} />
        <Route path="invoices" element={<AdminInvoicesPage />} />
        <Route path="messages" element={<AdminMessagesPage />} />
        <Route path="email-logs" element={<AdminEmailLogsPage />} />
        <Route path="notifications" element={<AdminNotificationsPage />} />
        <Route path="payments" element={<AdminPaymentsPage />} />
        <Route path="avis" element={<AdminReviewsPage />} />
        <Route path="reports" element={<AdminReportsPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
        <Route path="profile" element={<AdminProfilePage />} />
        <Route path="languages" element={<AdminLanguagesPage />} />
        <Route path="delivery" element={<AdminDeliveryPage />} />
        <Route path="services" element={<AdminServicesPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="clients" element={<AdminClientsPage />} />
        <Route path="clients/:id" element={<AdminClientProfilePage />} />
        <Route path="translators" element={<AdminTranslatorsPage />} />
        <Route path="translators/:id" element={<AdminTranslatorProfilePage />} />
        <Route path="verifiers" element={<AdminVerifiersPage />} />
        <Route path="verifiers/:id" element={<AdminVerifierProfilePage />} />
        <Route path="*" element={<PlaceholderPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
